import { useContext, useEffect, useMemo, useState } from 'react';
import { GlobalContext } from '../../context/GlobalContext';
import firestore from '../../firestore';
import {
	Bell,
	Calendar,
	Clock,
	CheckCircle,
	AlertCircle,
	Plus,
	Pencil,
	Save,
	Trash2,
	Send,
	Repeat,
	MessageSquare,
	Filter,
	Search,
	Copy,
	LayoutGrid,
	List,
	ArrowUp,
	ArrowDown,
	ArrowUpDown
} from 'lucide-react';
import { ContainerSpinner } from '../../components/Spinner';
import { Dialog } from '../../components/Dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import Select from '../../components/Select';
import { Button } from '../../shadcn/Button';
import { DateTimePicker } from '../../shadcn/DateTimePicker';
import { unsafeAPI, type API } from '../../hooks/api';

// Extract API types for type safety
type CreatePushNotificationRequest = API['https://api.krece.app/push_notif/']['POST']['request'];
type UpdatePushNotificationRequest = API['https://api.krece.app/push_notif/']['PATCH']['request'];

export interface PushNotification {
	id?: string; // Firebase document ID
	name: string;
	title: string;
	body: string;
	query: string;
	frequency: 'one_time' | 'daily' | 'weekly' | 'monthly';
	scheduled_date: { seconds: number; nanoseconds: number } | string;
	last_sent: { seconds: number; nanoseconds: number };
	status: 'pending' | 'in-progress' | 'completed' | 'sent';
	country: string;
}

// Edit Dialog Component
function EditPushNotificationDialog({
	notification,
	onClose,
	onSave,
	onDelete,
	saving,
	deleting,
	isNew
}: {
	notification: PushNotification;
	onClose: () => void;
	onSave: (updatedNotification: PushNotification, originalId: string | undefined) => void;
	onDelete?: (notificationId: string) => void;
	saving: boolean;
	deleting?: boolean;
	isNew?: boolean;
}) {
	const [editedNotification, setEditedNotification] = useState<PushNotification>({ ...notification });
	const originalId = notification.id;
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);

	const handleSave = () => {
		// Validate required fields
		if (!editedNotification.name?.trim()) {
			setAlertDialog({ title: 'Error', message: 'El nombre interno de la notificación es requerido' });
			return;
		}
		if (!editedNotification.title?.trim()) {
			setAlertDialog({ title: 'Error', message: 'El título de la notificación es requerido' });
			return;
		}
		if (!editedNotification.body?.trim()) {
			setAlertDialog({ title: 'Error', message: 'El mensaje de la notificación es requerido' });
			return;
		}
		if (!editedNotification.frequency) {
			setAlertDialog({ title: 'Error', message: 'La frecuencia es requerida' });
			return;
		}
		if (
			!editedNotification.scheduled_date ||
			(typeof editedNotification.scheduled_date === 'object' &&
				(!editedNotification.scheduled_date.seconds || editedNotification.scheduled_date.seconds <= 0)) ||
			(typeof editedNotification.scheduled_date === 'string' && !editedNotification.scheduled_date.trim())
		) {
			setAlertDialog({ title: 'Error', message: 'La fecha y hora programada es requerida' });
			return;
		}
		if (!editedNotification.query?.trim()) {
			setAlertDialog({ title: 'Error', message: 'El SQL query es requerido' });
			return;
		}
		// Validate SQL query contains SELECT keyword
		if (!editedNotification.query.trim().toUpperCase().includes('SELECT')) {
			setAlertDialog({ title: 'Error', message: 'El SQL ingresado es inválido' });
			return;
		}
		console.log('Saving push notification:', editedNotification);
		onSave(editedNotification, originalId);
	};

	const handleDelete = () => {
		setShowDeleteConfirm(true);
	};

	const handleConfirmDelete = async (): Promise<true | string> => {
		if (onDelete && originalId) {
			onDelete(originalId);
			return true;
		}
		return true;
	};

	return (
		<>
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{showDeleteConfirm && (
				<ConfirmDialog
					title="Eliminar Notificación Push"
					message="¿Estás seguro de que deseas eliminar esta notificación push? Esta acción no se puede deshacer."
					onConfirm={handleConfirmDelete}
					onClose={() => setShowDeleteConfirm(false)}
				/>
			)}
			<Dialog
				title=""
				onClose={onClose}
				closable={!saving ? true : false}
				cerrarButton={false}
				className="!w-[min(700px,95%)] !p-2 md:!p-6"
			>
				<div className="space-y-8 p-1 pb-20 md:pb-16">
					<Input
						title="Identificador Interno"
						placeholder="Ingrese un nombre único para identificar esta notificación"
						value={editedNotification.name}
						onChange={(value) => setEditedNotification((prev) => ({ ...prev, name: value }))}
					/>
					{/* Content Section */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-primary-500/10 p-2">
								<MessageSquare className="h-5 w-5 text-primary-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Contenido</h3>
						</div>

						<div className="grid gap-4">
							<Input
								floatingLabel="Título"
								placeholder="Ingrese el título de la notificación"
								value={editedNotification.title}
								onChange={(value) => setEditedNotification((prev) => ({ ...prev, title: value }))}
							/>

							<Textarea
								floatingLabel="Mensaje"
								placeholder="Ingrese el contenido de la notificación"
								value={editedNotification.body}
								onChange={(value) => setEditedNotification((prev) => ({ ...prev, body: value }))}
								rows={4}
								resize="vertical"
							/>
						</div>
					</div>

					{/* Scheduling Section */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-blue-500/10 p-2">
								<Calendar className="h-5 w-5 text-blue-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">Programación</h3>
						</div>

						<div className="grid gap-4">
							<Select
								floatingLabel="Frecuencia"
								options={[
									{ label: 'Una vez', value: 'one_time' },
									{ label: 'Diaria', value: 'daily' },
									{ label: 'Semanal', value: 'weekly' },
									{ label: 'Mensual', value: 'monthly' }
								]}
								defaultValue={editedNotification.frequency}
								onSelectedChange={(value) =>
									setEditedNotification((prev) => ({ ...prev, frequency: value as PushNotification['frequency'] }))
								}
							/>

							<DateTimePicker
								floatingLabel="Fecha y hora programada"
								defaultTimestamp={
									typeof editedNotification.scheduled_date === 'object' ? editedNotification.scheduled_date.seconds : undefined
								}
								onDateTimeChange={(utcString) => {
									setEditedNotification((prev) => ({
										...prev,
										scheduled_date: utcString || ''
									}));
								}}
								containerClassName="w-full"
								popoverContentClassName="pointer-events-auto"
							/>
						</div>
					</div>

					{/* Query Section */}
					<div className="space-y-6">
						<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
							<div className="rounded-lg bg-purple-500/10 p-2">
								<Filter className="h-5 w-5 text-purple-500" />
							</div>
							<h3 className="text-lg font-semibold text-neutral-100">SQL Query</h3>
						</div>

						<div className="space-y-2">
							<Textarea
								floatingLabel="SQL"
								placeholder="SELECT * FROM users WHERE country = 'venezuela'..."
								value={editedNotification.query}
								onChange={(value) => setEditedNotification((prev) => ({ ...prev, query: value }))}
								rows={8}
								resize="vertical"
								textareaClassName="font-mono"
								className="bg-neutral-900/50"
							/>
							<p className="text-xs text-neutral-400">Esta consulta determinará qué usuarios recibirán la notificación push.</p>
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="fixed bottom-6 right-6 z-50">
					<div className="flex items-center gap-2 rounded-full border border-neutral-700 bg-neutral-900/90 p-2 shadow-2xl backdrop-blur-md">
						{!isNew && onDelete && (
							<Button
								loading={deleting}
								variant="secondary"
								onClick={handleDelete}
								className="h-10 rounded-full px-4 text-sm font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
							>
								<Trash2 className="h-4 w-4" />
								Eliminar
							</Button>
						)}
						<Button
							loading={saving}
							variant="primary"
							onClick={handleSave}
							className="h-10 rounded-full px-4 text-sm font-medium"
						>
							<Save className="h-4 w-4" />
							{isNew ? 'Crear' : 'Guardar'}
						</Button>
					</div>
				</div>
			</Dialog>
		</>
	);
}

export default function PushNotifications() {
	const { internalCountry, userData } = useContext(GlobalContext);
	const country = internalCountry?.get() || 'venezuela';
	const [notifications, setNotifications] = useState<PushNotification[]>([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [expandedBodies, setExpandedBodies] = useState<Record<number, boolean>>({});
	const [expandedQueries, setExpandedQueries] = useState<Record<number, boolean>>({});
	const [editingNotification, setEditingNotification] = useState<PushNotification | null>(null);
	const [creatingNotification, setCreatingNotification] = useState(false);
	const [alertDialog, setAlertDialog] = useState<{ title: string; message: string } | null>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [statusFilter, setStatusFilter] = useState<string>('all');
	const [frequencyFilter, setFrequencyFilter] = useState<string>('all');
	const [viewMode, setViewMode] = useState<'cards' | 'table'>(() => {
		const saved = localStorage.getItem('push_notif_view_mode');
		return saved === 'cards' || saved === 'table' ? saved : 'table';
	});

	useEffect(() => {
		const unsubscribe = firestore
			.collection('PushNotificationsQueue')
			.where('country', '==', country)
			.onSnapshot(
				(snapshot) => {
					try {
						const notificationsData: PushNotification[] = [];

						snapshot.forEach((doc) => {
							const data = doc.data() as PushNotification;
							notificationsData.push({
								...data,
								// Include Firebase document ID
								id: doc.id,
								// Ensure all required fields exist with defaults
								title: data.title || doc.id,
								body: data.body || '',
								query: data.query || '',
								frequency: data.frequency || 'one_time',
								scheduled_date: data.scheduled_date || { seconds: 0, nanoseconds: 0 },
								last_sent: data.last_sent || { seconds: 0, nanoseconds: 0 },
								status: data.status || 'pending',
								country: data.country || country
							});
						});

						// Sort notifications: pending first, then by scheduled date
						notificationsData.sort((a, b) => {
							// Status priority: pending > in-progress > sent > completed
							const statusPriority = { pending: 0, 'in-progress': 1, sent: 2, completed: 3 };
							const aStatus = statusPriority[a.status] ?? 4;
							const bStatus = statusPriority[b.status] ?? 4;

							if (aStatus !== bStatus) {
								return aStatus - bStatus;
							}

							// Then by scheduled date (most recent first)
							const aTime =
								typeof a.scheduled_date === 'string'
									? new Date(a.scheduled_date).getTime() / 1000
									: (a.scheduled_date as { seconds: number; nanoseconds: number }).seconds;
							const bTime =
								typeof b.scheduled_date === 'string'
									? new Date(b.scheduled_date).getTime() / 1000
									: (b.scheduled_date as { seconds: number; nanoseconds: number }).seconds;

							return bTime - aTime;
						});

						setNotifications(notificationsData);
						setLoading(false);
					} catch (error) {
						console.error('Error processing push notifications snapshot:', error);
						setAlertDialog({
							title: 'Error',
							message: 'Error al procesar las notificaciones push'
						});
						setLoading(false);
					}
				},
				(error) => {
					console.error('Error listening to push notifications:', error);
					setAlertDialog({
						title: 'Error',
						message: 'Error al cargar las notificaciones push'
					});
					setLoading(false);
				}
			);

		// Cleanup subscription on unmount
		return () => unsubscribe();
	}, [country]);

	const toggleBody = (index: number) => {
		setExpandedBodies((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	const toggleQuery = (index: number) => {
		setExpandedQueries((prev) => ({ ...prev, [index]: !prev[index] }));
	};

	async function handleSaveNotification(updatedNotification: PushNotification, originalId: string | undefined) {
		setSaving(true);

		try {
			let response;

			if (!originalId) {
				// Creating new notification
				const createPayload = {
					name: updatedNotification.name, // Use title as identifier
					title: updatedNotification.title,
					body: updatedNotification.body,
					frequency: updatedNotification.frequency,
					scheduled_date:
						typeof updatedNotification.scheduled_date === 'string'
							? updatedNotification.scheduled_date
							: new Date(updatedNotification.scheduled_date.seconds * 1000).toISOString(),
					query: updatedNotification.query,
					country: updatedNotification.country
				} satisfies CreatePushNotificationRequest;

				response = await unsafeAPI('POST', 'https://api.krece.app/push_notif/', createPayload, userData, country);
			} else {
				// Updating existing notification - add original ID as identifier to URL
				const updatePayload = {
					name: updatedNotification.name, // Use title as identifier
					title: updatedNotification.title,
					body: updatedNotification.body,
					frequency: updatedNotification.frequency,
					scheduled_date:
						typeof updatedNotification.scheduled_date === 'string'
							? updatedNotification.scheduled_date
							: new Date(updatedNotification.scheduled_date.seconds * 1000).toISOString(),
					query: updatedNotification.query,
					country: updatedNotification.country
				} satisfies UpdatePushNotificationRequest;

				const updateUrl = `https://api.krece.app/push_notif/${encodeURIComponent(originalId)}/`;
				response = await unsafeAPI('PATCH', updateUrl, updatePayload, userData, country);
			}

			if (response && response.success !== false) {
				if (!originalId) {
					setCreatingNotification(false);
				}

				setEditingNotification(null);
				setAlertDialog({
					title: 'Éxito',
					message: !originalId ? 'Notificación creada correctamente' : 'Notificación actualizada correctamente'
				});
			} else {
				throw new Error(response?.status || 'Error desconocido');
			}
		} catch (error) {
			console.error('Error saving notification:', error);
			setAlertDialog({
				title: 'Error',
				message: error instanceof Error ? error.message : 'Error al guardar la notificación'
			});
		} finally {
			setSaving(false);
		}
	}

	async function handleDeleteNotification(notificationId: string) {
		setDeleting(true);

		try {
			// Add document ID as identifier to URL for deletion
			const deleteUrl = `https://api.krece.app/push_notif/${encodeURIComponent(notificationId)}/`;
			const response = await unsafeAPI('DELETE', deleteUrl, undefined, userData, country);

			if (response && response.success !== false) {
				setEditingNotification(null);
				setAlertDialog({
					title: 'Éxito',
					message: 'Notificación eliminada correctamente'
				});
			} else {
				throw new Error(response?.status || 'Error desconocido');
			}
		} catch (error) {
			console.error('Error deleting notification:', error);
			setAlertDialog({
				title: 'Error',
				message: error instanceof Error ? error.message : 'Error al eliminar la notificación'
			});
		} finally {
			setDeleting(false);
		}
	}

	const handleCreateNotification = () => {
		const newNotification: PushNotification = {
			name: '',
			title: '',
			body: '',
			query: '',
			frequency: 'one_time',
			scheduled_date: '', // Empty string - no default time
			last_sent: { seconds: 0, nanoseconds: 0 },
			status: 'pending',
			country: country
		};
		setEditingNotification(newNotification);
		setCreatingNotification(true);
	};

	const handleDuplicateNotification = (notification: PushNotification) => {
		const duplicated: PushNotification = {
			...notification,
			id: undefined,
			name: `${notification.name}_copia`,
			status: 'pending',
			last_sent: { seconds: 0, nanoseconds: 0 }
		};
		setEditingNotification(duplicated);
		setCreatingNotification(true);
	};

	const getStatusColor = (status: PushNotification['status']) => {
		switch (status) {
			case 'pending':
				return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
			case 'in-progress':
				return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
			case 'completed':
				return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
			case 'sent':
				return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
			default:
				return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
		}
	};

	const getStatusIcon = (status: PushNotification['status']) => {
		switch (status) {
			case 'pending':
				return <Clock className="mr-1 h-3 w-3" />;
			case 'in-progress':
				return <Send className="mr-1 h-3 w-3" />;
			case 'completed':
				return <CheckCircle className="mr-1 h-3 w-3" />;
			case 'sent':
				return <CheckCircle className="mr-1 h-3 w-3" />;
			default:
				return <AlertCircle className="mr-1 h-3 w-3" />;
		}
	};

	const getFrequencyLabel = (frequency: PushNotification['frequency']) => {
		switch (frequency) {
			case 'one_time':
				return 'Una vez';
			case 'daily':
				return 'Diaria';
			case 'weekly':
				return 'Semanal';
			case 'monthly':
				return 'Mensual';
			default:
				return frequency;
		}
	};

	const getStatusLabel = (status: PushNotification['status']) => {
		switch (status) {
			case 'pending': return 'Pendiente';
			case 'in-progress': return 'Enviando...';
			case 'completed': return 'Completada';
			case 'sent': return 'Enviada';
			default: return status;
		}
	};

	// Filter notifications based on search query and filters
	const filteredNotifications = notifications.filter((notification) => {
		if (statusFilter !== 'all' && notification.status !== statusFilter) return false;
		if (frequencyFilter !== 'all' && notification.frequency !== frequencyFilter) return false;

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			return (
				notification.title.toLowerCase().includes(query) ||
				notification.body.toLowerCase().includes(query) ||
				notification.name?.toLowerCase().includes(query) ||
				notification.query.toLowerCase().includes(query)
			);
		}

		return true;
	});

	const hasActiveFilters = statusFilter !== 'all' || frequencyFilter !== 'all';

	const clearFilters = () => {
		setStatusFilter('all');
		setFrequencyFilter('all');
		setSearchQuery('');
	};

	type SortKey = 'name' | 'status' | 'frequency' | 'scheduled' | 'lastSent';
	const [sortKey, setSortKey] = useState<SortKey | null>(null);
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

	const handleSort = (key: SortKey) => {
		if (sortKey === key) {
			setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
		} else {
			setSortKey(key);
			setSortDir('asc');
		}
	};

	const getScheduledSeconds = (n: PushNotification) => {
		if (typeof n.scheduled_date === 'string' && n.scheduled_date.trim()) return new Date(n.scheduled_date).getTime() / 1000;
		if (typeof n.scheduled_date === 'object' && n.scheduled_date.seconds > 0) return n.scheduled_date.seconds;
		return 0;
	};

	const sortedNotifications = useMemo(() => {
		if (!sortKey) return filteredNotifications;
		const dir = sortDir === 'asc' ? 1 : -1;
		return [...filteredNotifications].sort((a, b) => {
			switch (sortKey) {
				case 'name':
					return dir * (a.name || a.title).localeCompare(b.name || b.title);
				case 'status': {
					const order = { pending: 0, 'in-progress': 1, sent: 2, completed: 3 };
					return dir * ((order[a.status] ?? 4) - (order[b.status] ?? 4));
				}
				case 'frequency': {
					const order = { one_time: 0, daily: 1, weekly: 2, monthly: 3 };
					return dir * ((order[a.frequency] ?? 4) - (order[b.frequency] ?? 4));
				}
				case 'scheduled':
					return dir * (getScheduledSeconds(a) - getScheduledSeconds(b));
				case 'lastSent':
					return dir * ((a.last_sent?.seconds ?? 0) - (b.last_sent?.seconds ?? 0));
				default:
					return 0;
			}
		});
	}, [filteredNotifications, sortKey, sortDir]);

	const formatScheduledDate = (n: PushNotification) => {
		const seconds = getScheduledSeconds(n);
		if (!seconds) return null;
		return new Date(seconds * 1000).toLocaleString('es-ES', {
			year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
		});
	};

	if (loading) {
		return <ContainerSpinner size={3} />;
	}

	return (
		<div className="mx-auto w-full max-w-7xl space-y-4 p-6">
			{alertDialog && (
				<Dialog title={alertDialog.title} onClose={() => setAlertDialog(null)} closable={true}>
					<p className="text-neutral-200">{alertDialog.message}</p>
				</Dialog>
			)}
			{editingNotification && (
				<EditPushNotificationDialog
					notification={editingNotification}
					onClose={() => {
						setEditingNotification(null);
						setCreatingNotification(false);
					}}
					onSave={handleSaveNotification}
					onDelete={!creatingNotification ? handleDeleteNotification : undefined}
					saving={saving}
					deleting={deleting}
					isNew={creatingNotification}
				/>
			)}

			{/* Search & Toolbar */}
			<div className="flex items-center gap-2">
				<Input
					placeholder="Buscar notificaciones..."
					value={searchQuery}
					onChange={setSearchQuery}
					Icon={<Search className="h-4 w-4 text-neutral-400" />}
					containerClassName="flex-1"
				/>
				<div className="flex rounded-lg border border-neutral-800 overflow-hidden shrink-0">
					<button
						onClick={() => { setViewMode('cards'); localStorage.setItem('push_notif_view_mode', 'cards'); }}
						className={`p-2 transition-colors ${viewMode === 'cards' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'}`}
						title="Vista de tarjetas"
					>
						<LayoutGrid className="h-4 w-4" />
					</button>
					<button
						onClick={() => { setViewMode('table'); localStorage.setItem('push_notif_view_mode', 'table'); }}
						className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-neutral-800 text-neutral-100' : 'text-neutral-500 hover:text-neutral-300'}`}
						title="Vista de tabla"
					>
						<List className="h-4 w-4" />
					</button>
				</div>
				<button
					onClick={handleCreateNotification}
					className="shrink-0 flex items-center gap-2 rounded-lg bg-primary-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
				>
					<Plus className="h-4 w-4" />
					<span className="hidden md:inline">Agregar Notificación</span>
				</button>
			</div>

			{/* Filters */}
			<div className="grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center md:gap-3">
				<Select
					options={[
						{ label: 'Todos los estados', value: 'all' },
						{ label: 'Pendiente', value: 'pending' },
						{ label: 'Enviando...', value: 'in-progress' },
						{ label: 'Enviada', value: 'sent' },
						{ label: 'Completada', value: 'completed' }
					]}
					defaultValue={statusFilter}
					onSelectedChange={(v) => setStatusFilter((v as string) || 'all')}
					isDefaultValueNull={false}
					containerClassName="min-w-0"
				/>
				<Select
					options={[
						{ label: 'Todas las frecuencias', value: 'all' },
						{ label: 'Una vez', value: 'one_time' },
						{ label: 'Diaria', value: 'daily' },
						{ label: 'Semanal', value: 'weekly' },
						{ label: 'Mensual', value: 'monthly' }
					]}
					defaultValue={frequencyFilter}
					onSelectedChange={(v) => setFrequencyFilter((v as string) || 'all')}
					isDefaultValueNull={false}
					containerClassName="min-w-0"
				/>
				{hasActiveFilters && (
					<button
						onClick={clearFilters}
						className="text-xs text-primary-400 hover:text-primary-300 transition-colors text-center"
					>
						Limpiar filtros
					</button>
				)}
			</div>

			{/* Main Content Container */}
			{notifications.length === 0 ? (
				<div className="py-12 text-center">
					<Bell className="mx-auto h-12 w-12 text-neutral-400" />
					<h3 className="mt-4 text-lg font-semibold text-neutral-200">No hay notificaciones push</h3>
					<p className="text-neutral-400">Comienza creando tu primera notificación push.</p>
				</div>
			) : filteredNotifications.length === 0 ? (
				<div className="py-12 text-center">
					<Filter className="mx-auto h-12 w-12 text-neutral-400" />
					<h3 className="mt-4 text-lg font-semibold text-neutral-200">No se encontraron resultados</h3>
					<p className="text-neutral-400">No hay notificaciones que coincidan con los filtros aplicados.</p>
				</div>
			) : viewMode === 'table' ? (
				<div className="overflow-x-auto rounded-lg border border-neutral-800">
					<table className="w-full text-sm">
						<thead>
							<tr className="border-b border-neutral-800 bg-neutral-900">
								{([
									['name', 'Nombre', ''],
									['status', 'Estado', ''],
									['frequency', 'Frecuencia', 'hidden md:table-cell'],
									['scheduled', 'Programada', 'hidden md:table-cell'],
									['lastSent', 'Último envío', 'hidden lg:table-cell'],
								] as [SortKey, string, string][]).map(([key, label, hide]) => (
									<th
										key={key}
										onClick={() => handleSort(key)}
										className={`px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400 cursor-pointer select-none hover:text-neutral-200 transition-colors ${hide}`}
									>
										<div className="flex items-center gap-1">
											{label}
											{sortKey === key ? (
												sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
											) : (
												<ArrowUpDown className="h-3 w-3 opacity-30" />
											)}
										</div>
									</th>
								))}
								<th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-400 hidden xl:table-cell">Contenido</th>
								<th className="px-4 py-3 text-right text-[11px] font-semibold uppercase tracking-wider text-neutral-400 w-20" />
							</tr>
						</thead>
						<tbody>
							{sortedNotifications.map((notification, index) => (
								<tr
									key={index}
									onClick={() => setEditingNotification(notification)}
									className="border-b border-neutral-800/50 last:border-b-0 cursor-pointer transition-colors hover:bg-neutral-800/20"
								>
									<td className="px-4 py-3">
										<div className="font-medium text-neutral-100 truncate max-w-[200px]">{notification.name || notification.title}</div>
										<div className="text-xs text-neutral-500 truncate max-w-[200px]">{notification.title}</div>
									</td>
									<td className="px-4 py-3">
										<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(notification.status)}`}>
											{getStatusLabel(notification.status)}
										</span>
									</td>
									<td className="px-4 py-3 hidden md:table-cell text-neutral-300">
										{getFrequencyLabel(notification.frequency)}
									</td>
									<td className="px-4 py-3 hidden md:table-cell">
										<span className={formatScheduledDate(notification) ? 'text-neutral-300' : 'text-neutral-600 italic'}>
											{formatScheduledDate(notification) || '-'}
										</span>
									</td>
									<td className="px-4 py-3 hidden lg:table-cell">
										<span className={notification.last_sent?.seconds > 0 ? 'text-neutral-300' : 'text-neutral-600'}>
											{notification.last_sent?.seconds > 0
												? new Date(notification.last_sent.seconds * 1000).toLocaleString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
												: '-'}
										</span>
									</td>
									<td className="px-4 py-3 hidden xl:table-cell">
										<div className="text-xs text-neutral-400 truncate max-w-[250px]">{notification.body}</div>
									</td>
									<td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
										<div className="flex items-center justify-end gap-1">
											<button
												onClick={() => handleDuplicateNotification(notification)}
												className="rounded-full p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300"
												title="Duplicar"
											>
												<Copy className="h-3.5 w-3.5" />
											</button>
											<button
												onClick={() => setEditingNotification(notification)}
												className="rounded-full p-1.5 text-neutral-500 transition-colors hover:bg-neutral-800 hover:text-neutral-300"
												title="Editar"
											>
												<Pencil className="h-3.5 w-3.5" />
											</button>
										</div>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<div className="grid gap-4 min-[850px]:grid-cols-2">
					{sortedNotifications.map((notification, index) => (
						<div
							key={index}
							className="rounded-lg border border-neutral-900 bg-neutral-1000 p-4 dark:border-neutral-800 dark:bg-background"
						>
							{/* Header with name, edit button and status */}
							<div className="mb-4 flex items-start justify-between gap-2">
								<div className="flex-grow">
									<h2
										className="h-[45px] text-lg font-semibold leading-tight text-neutral-200 dark:text-neutral-300"
										style={{
											display: '-webkit-box',
											WebkitLineClamp: expandedBodies[index] ? 'unset' : 2,
											WebkitBoxOrient: 'vertical',
											overflow: expandedBodies[index] ? 'visible' : 'hidden'
										}}
									>
										{notification.name || notification.title}
									</h2>
								</div>
								<div className="flex shrink-0 items-center gap-2">
									<button
										onClick={() => handleDuplicateNotification(notification)}
										className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-200"
										title="Duplicar notificación"
									>
										<Copy className="h-3.5 w-3.5" />
										Duplicar
									</button>
									<button
										onClick={() => setEditingNotification(notification)}
										className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-neutral-200"
										title="Editar notificación"
									>
										<Pencil className="h-3.5 w-3.5" />
										Editar
									</button>
									<span
										className={`flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(notification.status)}`}
									>
										{getStatusIcon(notification.status)}
										{notification.status === 'pending'
											? 'Pendiente'
											: notification.status === 'in-progress'
												? 'Enviando...'
												: notification.status === 'completed'
													? 'Completada'
													: notification.status === 'sent'
														? 'Enviada'
														: notification.status}
									</span>
								</div>
							</div>

							<div className="space-y-3">
								{/* Title Section */}
								<div className="mb-3">
									<div className="mb-1 flex items-center text-sm font-medium text-neutral-100 dark:text-neutral-200">
										<Bell className="mr-2 h-4 w-4 text-primary-500" />
										<span>Título:</span>
									</div>
									<p className="text-sm font-medium text-neutral-200 dark:text-neutral-300">{notification.title}</p>
								</div>

								{/* Body */}
								<div className="mb-3">
									<div className="mb-1 flex items-center text-sm font-medium text-neutral-100 dark:text-neutral-200">
										<MessageSquare className="mr-2 h-4 w-4 text-primary-500" />
										<span>Contenido:</span>
									</div>
									<div className={`relative ${expandedBodies[index] ? 'min-h-10' : 'h-10'}`}>
										<p
											className={`cursor-pointer text-sm text-neutral-300 transition-all duration-200 hover:opacity-80 dark:text-neutral-400 ${expandedBodies[index] ? 'line-clamp-none' : 'line-clamp-2 overflow-hidden'}`}
											onClick={() => toggleBody(index)}
											style={{
												display: '-webkit-box',
												WebkitLineClamp: expandedBodies[index] ? 'unset' : 2,
												WebkitBoxOrient: 'vertical',
												overflow: expandedBodies[index] ? 'visible' : 'hidden'
											}}
											title="Click para expandir/contraer"
										>
											{notification.body}
										</p>
									</div>
								</div>

								{/* Frequency */}
								<div className="mb-3 flex items-center text-sm">
									<Repeat className="mr-2 h-4 w-4 flex-shrink-0 text-primary-500" />
									<span className="font-medium text-neutral-100 dark:text-neutral-200">Frecuencia:&nbsp;</span>
									<span className="text-neutral-300 dark:text-neutral-400">{getFrequencyLabel(notification.frequency)}</span>
								</div>

								{/* Scheduled Date */}
								<div className="mb-3 flex items-center text-sm">
									<Calendar className="mr-2 h-4 w-4 flex-shrink-0 text-primary-500" />
									<span className="font-medium text-neutral-100 dark:text-neutral-200">Programada:&nbsp;</span>
									<span className="text-neutral-300 dark:text-neutral-400">
										{(() => {
											if (typeof notification.scheduled_date === 'string' && notification.scheduled_date.trim()) {
												return new Date(notification.scheduled_date).toLocaleString('es-ES', {
													year: 'numeric',
													month: 'short',
													day: 'numeric',
													hour: '2-digit',
													minute: '2-digit'
												});
											} else if (typeof notification.scheduled_date === 'object' && notification.scheduled_date.seconds > 0) {
												return new Date(notification.scheduled_date.seconds * 1000).toLocaleString('es-ES', {
													year: 'numeric',
													month: 'short',
													day: 'numeric',
													hour: '2-digit',
													minute: '2-digit'
												});
											}
											return 'No programada';
										})()}
									</span>
								</div>

								{/* Last Sent */}
								{notification.last_sent.seconds > 0 && (
									<div className="mb-3 flex items-center text-sm">
										<Send className="mr-2 h-4 w-4 flex-shrink-0 text-green-500" />
										<span className="font-medium text-neutral-100 dark:text-neutral-200">Último envío:&nbsp;</span>
										<span className="text-neutral-300 dark:text-neutral-400">
											{new Date(notification.last_sent.seconds * 1000).toLocaleString('es-ES', {
												year: 'numeric',
												month: 'short',
												day: 'numeric',
												hour: '2-digit',
												minute: '2-digit'
											})}
										</span>
									</div>
								)}

								{/* Query - Moved to bottom */}
								<div className="mt-4 border-t border-neutral-800/50 pt-3">
									<div className="mb-1 flex items-center text-sm font-medium text-neutral-100 dark:text-neutral-200">
										<Filter className="mr-2 h-4 w-4 text-neutral-500" />
										<span className="text-neutral-400">SQL Query:</span>
									</div>
									<div className={`relative ${expandedQueries[index] ? 'min-h-8' : 'h-8'}`}>
										<p
											className={`cursor-pointer font-mono text-xs text-neutral-400 transition-all duration-200 hover:opacity-80 dark:text-neutral-500 ${expandedQueries[index] ? 'line-clamp-none' : 'line-clamp-2 overflow-hidden'}`}
											onClick={() => toggleQuery(index)}
											style={{
												display: '-webkit-box',
												WebkitLineClamp: expandedQueries[index] ? 'unset' : 2,
												WebkitBoxOrient: 'vertical',
												overflow: expandedQueries[index] ? 'visible' : 'hidden'
											}}
											title="Click para expandir/contraer"
										>
											{notification.query}
										</p>
									</div>
								</div>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
